import React, { useRef } from 'react'
import {
  Column,
  ColumnChooser,
  ColumnChooserSearch,
  DataGrid,
  DataGridRef,
  DataGridTypes,
  Editing,
  Export,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Paging,
  RequiredRule,
  Scrolling,
  SearchPanel,
  Sorting,
  StateStoring,
  StringLengthRule,
} from 'devextreme-react/data-grid'
import ODataStore from 'devextreme/data/odata/store'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import { useScreenSize } from '@/themes/media-query'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { ad_incentivo } from '@/model/qsadminapi/QsAdminApiModuleModel'
import {
  applyPendingChanges,
  notifySuccess,
  storeErrorHandler,
  validateBeforeSaving,
} from '@/routes/admin/autodesk/autodesk.utils'

const NOME_MAX_LENGTH = 20

export const Incentivi = () => {
  const token = useTokenRefresh()
  const { service, client } = useQsAdminApiManager()
  const dataGridRef = useRef<DataGridRef>(null)
  const currentScreenSize = useScreenSize()

  const incentiviDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/ad_incentivo`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      errorHandler: storeErrorHandler,
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    expand: ['commissioni'],
    requireTotalCount: true,
  }

  // Il nome viene confrontato con il programma del CSV Autodesk nell'export commissioni: deve essere univoco
  const findDuplicateNome = async (changes: DataGridTypes.DataChange[]) => {
    const response = await client.get(`${service.getPath()}/ad_incentivo?$select=id,nome`)
    const rows = applyPendingChanges<ad_incentivo>(response.data.value, changes)
    for (const row of rows.filter((r) => r.touched)) {
      const nome = row.data.nome?.trim().toLowerCase()
      if (!nome) continue
      if (rows.filter((r) => r.data.nome?.trim().toLowerCase() === nome).length > 1)
        return `L'incentivo "${row.data.nome}" esiste già`
    }
  }

  return (
    <div className="management-grid content-block">
      <h2>Incentivi Autodesk</h2>

      <DataGrid
        dataSource={incentiviDataSource}
        height="75vh"
        className={currentScreenSize.isSmall ? 'grid-height-small' : 'grid-height-default'}
        noDataText="Nessun incentivo trovato"
        showBorders={false}
        remoteOperations={{
          filtering: true,
          sorting: true,
          paging: true,
        }}
        showColumnLines={true}
        focusedRowEnabled={false}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall}
        allowColumnResizing={currentScreenSize.isLarge || currentScreenSize.isMedium}
        allowColumnReordering={currentScreenSize.isLarge || currentScreenSize.isMedium}
        rowAlternationEnabled={true}
        wordWrapEnabled={true}
        onSaving={(e) => validateBeforeSaving(e, () => findDuplicateNome(e.changes))}
        onRowInserted={() => notifySuccess('Created')}
        onRowUpdated={() => notifySuccess('Updated')}
        onRowRemoved={() => notifySuccess('Deleted')}
        ref={dataGridRef}
      >
        <Editing
          mode="batch"
          allowAdding={true}
          allowUpdating={true}
          // Un incentivo usato da almeno una commissione non può essere eliminato
          allowDeleting={(e: { row?: DataGridTypes.Row }) => !e.row?.data?.commissioni?.length}
          useIcons={true}
        />

        <StateStoring enabled={true} type="localStorage" storageKey="ad-incentivi-grid" savingTimeout={50} />

        <HeaderFilter visible />
        <FilterPanel visible />
        <FilterRow visible />
        <SearchPanel visible width={currentScreenSize.isXSmall ? 200 : 250} />
        <Scrolling mode="virtual" />
        <Sorting mode="multiple" />
        <Export enabled />
        <ColumnChooser enabled>
          <ColumnChooserSearch enabled />
        </ColumnChooser>

        <Paging defaultPageSize={20} enabled />

        <Column dataField="id" caption="ID" dataType="number" width={80} allowEditing={false} visible={false} />

        <Column dataField="nome" caption="Nome" dataType="string" defaultSortOrder="asc">
          <RequiredRule message="Nome obbligatorio" />
          <StringLengthRule
            max={NOME_MAX_LENGTH}
            message={`Il nome può avere al massimo ${NOME_MAX_LENGTH} caratteri`}
          />
        </Column>

        <Column dataField="note" caption="Note" dataType="string" hidingPriority={1} />

        <Column
          caption="Commissioni"
          dataType="number"
          allowEditing={false}
          allowSorting={false}
          allowFiltering={false}
          width={130}
          calculateCellValue={(rowData: ad_incentivo) => rowData.commissioni?.length ?? 0}
          hidingPriority={2}
        />
      </DataGrid>
    </div>
  )
}
